import { PropertyState } from "@animech/axiom-configurator";
import {
	PartialRootPropertyUpdateTree,
	StateTree,
} from "@animech/axiom-format";
import { Configurator } from "./definitions-utility";
import { Root, root } from "src/definitions";

/**
 * THE restore primitive for Grythyttan. Every stored-configuration restore
 * (Open Design, undo/redo, localStorage, viewer designtemplate) MUST go
 * through this function. See ADR-0005.
 *
 * Why this exists (and why String doesn't need it):
 * Grythyttan's rules cascade — e.g. changing tableType triggers rules
 * that overwrite chair properties. A single `configurator.set()` loses
 * those values. This loop re-applies only the remaining diff each pass;
 * by pass 2 the trigger property is already correct so the overwriting
 * rule doesn't re-fire, and the values stick. String's rules don't
 * cascade this way, so it can use a single `set()`.
 *
 * Requires a full state tree — partial deltas crash getDelta. All callers
 * (undo/redo, Open Design, localStorage, viewer designtemplate) provide
 * full trees because sessionDataDeltaType is "full".
 */
export function restoreStoredConfiguration(
	configurator: Configurator,
	targetTree: PartialRootPropertyUpdateTree<Root>,
): void {
	// History snapshots strip uiSection (see _addToHistory). Without
	// backfilling it, getDelta sees the missing key as a deletion and
	// resets accordion state on every undo/redo.
	const tree = targetTree as Record<string, unknown>;
	if (!("uiSection" in tree)) {
		tree.uiSection = configurator.get(root.uiSection).stateTree;
	}

	const maxPasses = 5;
	for (let pass = 0; pass < maxPasses; pass++) {
		const currentState = configurator.get(root);
		const targetState = new PropertyState(root, targetTree as StateTree<Root>);
		const delta = currentState.getDelta(targetState);
		if (delta.stateMap.size === 0) {
			break;
		}
		configurator.set(root, delta.stateTree);
	}
}
